import React, { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Table, T, VStack } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { SettlementPaymentTransferRecordTaskFeeFragment } from 'types/graphql'

import { formatFee } from 'src/utils/fee'

type Props = {
  fees: SettlementPaymentTransferRecordTaskFeeFragment[]
}

const SettlementTransactionExpandedFeesTable = ({ fees }: Props) => {
  const columnHelper = useMemo(
    () => createColumnHelper<SettlementPaymentTransferRecordTaskFeeFragment>(),
    []
  )

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row, {
        id: 'feeName',
        header: 'Fee',
        cell: (info) => {
          const rtf = info.getValue()
          const name = rtf.feeVersion?.fee?.name ?? '—'
          const description = rtf.description ?? null
          return (
            <VStack>
              <T.Body>{name}</T.Body>
              {description && <T.BodySmall>{description}</T.BodySmall>}
            </VStack>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.totalWhenInvoiceRequestedCents, {
        id: 'amount',
        header: 'Revenue',
        cell: (info) => {
          const value = info.getValue()
          if (typeof value !== 'number') {
            return <T.Body>—</T.Body>
          }
          return <T.Body>{formatFee(value)}</T.Body>
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.feeVersion?.generalLedgerNumber?.glNumber ?? '', {
        id: 'glCode',
        header: 'GL Code',
        cell: (info) => <T.Body>{info.getValue() || '—'}</T.Body>,
        enableSorting: false,
      }),
    ]
  }, [columnHelper])

  const getRowId = useCallback(
    (row: SettlementPaymentTransferRecordTaskFeeFragment) => `${row.id}`,
    []
  )

  const tableProps = useTableState<SettlementPaymentTransferRecordTaskFeeFragment>()

  if (!fees.length) {
    return null
  }

  return (
    <Table
      columns={columns}
      data={fees}
      enablePagination={false}
      getRowId={getRowId}
      shouldHideHeader={false}
      {...tableProps}
    />
  )
}

export default React.memo(SettlementTransactionExpandedFeesTable)
